import * as React from "react";
import { IconSSR } from "../Icon/IconSSR";

export type ColorMode = "system" | "light" | "dark";

export type TopNavProps = {
  /**
   * Content for the right side (avatar, actions, etc.)
   * @deprecated Use centerContent and actions instead for better layout control
   */
  rightContent?: React.ReactNode;
  /**
   * Content for the center area (typically search)
   * On mobile, this moves to rightContent position
   */
  centerContent?: React.ReactNode;
  showMobileMenu?: boolean;
  avatar?: React.ReactNode;
  showColorModeSwitcher?: boolean;
  toggleTheme?: () => void;
  isDarkThemeEnabled?: boolean;
  colorMode?: ColorMode;
  actions?: React.ReactNode;
  className?: string;
  mobileMenuId?: string;
};

/**
 * TopNav
 *
 * Horizontal navigation bar component for application headers.
 * Includes mobile menu toggle, user avatar, theme switcher, and customizable action areas.
 */
export const TopNav: React.FC<TopNavProps> = ({
  rightContent,
  centerContent,
  showMobileMenu = true,
  avatar,
  showColorModeSwitcher = false,
  toggleTheme,
  isDarkThemeEnabled = false,
  colorMode,
  actions,
  className = "",
  mobileMenuId = "app-mobile-menu",
}) => {
  const getThemeIcon = () => {
    const effectiveMode = colorMode || (isDarkThemeEnabled ? "dark" : "light");

    switch (effectiveMode) {
      case "system":
        return <IconSSR name="Monitor" size="sm" />;
      case "light":
        return <IconSSR name="Sun" size="sm" />;
      case "dark":
        return <IconSSR name="Moon" size="sm" />;
    }
  };

  const getAriaLabel = () => {
    const effectiveMode = colorMode || (isDarkThemeEnabled ? "dark" : "light");

    switch (effectiveMode) {
      case "system":
        return "Switch to light mode";
      case "light":
        return "Switch to dark mode";
      case "dark":
        return "Switch to system mode";
      default:
        return "Toggle theme";
    }
  };
  return (
    <header className={`h-[60px] bg-background-surface border-b ${className}`}>
      <div className="relative flex h-full items-center">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center h-full flex-shrink-0">
          {showMobileMenu && (
            <label htmlFor={mobileMenuId} className="md:hidden cursor-pointer" aria-label="Open menu">
              <div className="flex h-full w-14 flex-col items-center justify-center border-r hover:bg-background-hover transition-colors">
                <IconSSR name="List" size="sm" />
              </div>
            </label>
          )}
        </div>

        {/* Center content - responsive layout */}
        {centerContent && (
          <div className="flex-1 flex justify-end md:justify-center md:px-4 min-w-0">
            <div className="w-full md:max-w-4xl min-w-0">{centerContent}</div>
          </div>
        )}

        {/* Spacer when no center content */}
        {!centerContent && <div className="flex-1" />}

        {/* Right side - User actions */}
        <div className="flex items-center gap-4 pr-4 flex-shrink-0 ml-4">
          {/* Legacy right content (deprecated) */}
          {rightContent}

          {/* Theme switcher */}
          {showColorModeSwitcher && toggleTheme && (
            <button
              type="button"
              onClick={toggleTheme}
              className="flex items-center justify-center w-8 h-8 rounded hover:bg-background-hover transition-colors"
              aria-label={getAriaLabel()}
            >
              {getThemeIcon()}
            </button>
          )}

          {/* Additional actions */}
          {actions}

          {/* Avatar */}
          {avatar && <div className="flex items-center">{avatar}</div>}
        </div>
      </div>
    </header>
  );
};
